import React, { MouseEvent } from "react";
import dynamic from "next/dynamic";
import { useParams, usePathname, useSearchParams } from "next/navigation";

// components
import BaseHeader from "../BaseHeader";
import HirerHeaderMobile from "./containers/HirerHeaderMobile";
const HeaderNavigation = dynamic(() => import("./components/HeaderNavigation"), { ssr: false });
import HeaderAuthPopover from "@/components/HeaderAuthPopover";
import notification from "../../../components/notifications";
import HeaderCurrencySelector from "./components/HeaderCurrencySelector";
import HirerHeaderLogo from "./components/HirerHeaderLogo";
import HeaderProfileDropdown from "@/components/HeaderProfileDropdown";
import HeaderRegionCurrencyDropdown from "@/components/HeaderRegionCurrencyDropdown";
import Button from "@/shared/Button";

//helpers
import withPageTitleNotification from "@/hoc/withPageTitleNotification";
import useUnreadMessages from "@/hooks/useUnreadMessages";
import useUserProfile from "@/hooks/useUserProfile";
import { useDimension } from "@/hooks/useDimensions";
import { HIRER_NAVIGATION_ROUTES_DESK } from "../constants";
import { useLockoutModalContext } from "@/contexts/LockoutModalContext";
import customDataLayerPush from "@/helpers/customDataLayerPush";
import account from "@/api/account";
import locations from "@/api/locations";
import spacesApi from "@/api/spaces";
import useRouter from "@/hooks/useRouter";
import { hirerLocationPageRegex, hirerSpacePageRegex } from "@/globals/pathnameRegex.constants";


// interfaces
import { IHeaderNavigationLink } from "./components/IHeaderNavigation";

//assets
import styles from "@/assets/styles/components/Header/HirerHeader/index.module.scss"


type THirerHeader = {
  isAuthorized?: boolean;
}

function HirerHeader({ isAuthorized: initIsAuthorized }: THirerHeader) {
  const { isAuthorized, data, permissions } = useUserProfile({ initIsAuthorized });
  const [width] = useDimension();
  const { setState } = useLockoutModalContext();

  const { push } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = useParams();

  const isLocationPage = hirerLocationPageRegex.test(pathname);
  const isSpacePage = hirerSpacePageRegex.test(pathname);

  const [unreadMessages] = useUnreadMessages({
    onNudgeMessage: (message: string) => notification({
      type: "info",
      placement: "bottomLeft",
      duration: 5,
      description: message,
    })
  });

  const isTablet = !!width && width < 1200;
  const navigationRoutesToUse = HIRER_NAVIGATION_ROUTES_DESK
    .filter(item => (item as IHeaderNavigationLink).route === "/bookings" ? !isAuthorized || permissions.hirer.canAccessBookingsHistory : true)
    .map(item => (item as IHeaderNavigationLink).route === "/my-messages" ? { ...item, badge: unreadMessages } : item);

  // methods
  const logoutUser = async (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();

    await account.logOut();
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, route: string) => {
    if (!isAuthorized && !["/go-search", "/search-results", "/lolive"].includes(route) && route !== "/") {
      e.preventDefault();
      setState({ open: true, loginCallbackUrl: route });
      customDataLayerPush("lo_fo_modal_popup_shown", null, { lofo_type: "soft lofo" });
    }
  };

  const handleSendAnalytics = () => {
    if (!isLocationPage && !isSpacePage) {
      return;
    }

    const isPreview = searchParams.get("preview");

    if (isPreview) {
      return;
    }

    if (isLocationPage) {
      const slug = params.slug as string;
      locations.getLocationsRecordPageView(slug as string).catch(() => null)
    }
    
    if (isSpacePage) {
      const spaceSlug = params.spaceSlug as string;
      spacesApi.getSpaceRecordPageView(spaceSlug).catch(() => null);
    }
  }

  const handleLogin = (userType: string) => {
    if (userType === "hirer") {
      handleSendAnalytics();

      return;
    }

    push("/lp");
  }

  // renders
  const renderHeaderProfile = () => (
    isAuthorized
      ? <HeaderProfileDropdown user={data} onLogout={logoutUser} key="header-profile"/>
      : (
        <div className={styles["locl-hirer-header__auth-buttons"]}>
          <HeaderAuthPopover onSubmit={handleLogin} key="header-header-popover"/>
          <Button danger to="/registration">
            Sign up, it’s free!
          </Button>
        </div>
      )
  );

  return (
    <BaseHeader
      className={styles["locl-hirer-header"]}
      data-testid="locl-hirer-header"
      renderDrawerContent={(onClose: () => void) => (
        <HirerHeaderMobile
          onClose={onClose}
          onLogout={logoutUser}
          unreadMessages={unreadMessages}
        />
      )}
    >
      <BaseHeader.Logo>
        <HirerHeaderLogo />
      </BaseHeader.Logo>
      {
        !isTablet && (
          <>
            <HeaderNavigation
              key="header-navigation"
              navigationLinks={navigationRoutesToUse as IHeaderNavigationLink[]}
              data-testid="locl-hirer-header-navigation"
              onClick={handleLinkClick}
            />
            <div className={styles["locl-hirer-header-options"]}>
              { process.env.NEXT_PUBLIC_APP_WL_CODE === "LOCL"
                ? <HeaderRegionCurrencyDropdown />
                : <HeaderCurrencySelector />
              }
            </div>
          </>
        )
      }
      { !!isTablet && (
        <div className={styles["locl-hirer-header-options"]}>
          { process.env.NEXT_PUBLIC_APP_WL_CODE === "LOCL"
            ? <HeaderRegionCurrencyDropdown />
            : <HeaderCurrencySelector isMobile />
          }
        </div>
      )}
      <BaseHeader.Profile>
        { !isTablet && renderHeaderProfile()}
      </BaseHeader.Profile>
    </BaseHeader>
  );
}

export default withPageTitleNotification(HirerHeader);
